// frontend/src/services/excelProcessing.service.ts - WITH ATTENDANCE VALIDATION
import * as XLSX from 'xlsx';

export interface ExcelProcessingResult {
  success: boolean;
  auxNumbers: string[];
  errors: string[];
  totalRows: number;
  validRows: number;
}

export interface EmployeeMatchResult {
  matched: any[];
  failed: string[];
  total: number;
  success: boolean;
  // 🆕 NEW: Attendance validation fields
  matchedWithAttendance?: any[];
  matchedWithoutAttendance?: any[];
  attendanceValidated?: boolean;
}

class ExcelProcessingService {
  /**
   * Process uploaded Excel file and extract AUX numbers
   */
  async processExcelFile(file: File): Promise<ExcelProcessingResult> {
    try {
      console.log('Processing Excel file:', file.name);
      
      // Read file as array buffer
      const data = await file.arrayBuffer();
      
      // Parse Excel file
      const workbook = XLSX.read(data, { 
        type: 'array',
        cellDates: true,
        cellFormula: false,
        cellStyles: false
      });

      // Get first sheet
      const sheetName = workbook.SheetNames[0];
      if (!sheetName) {
        throw new Error('No sheets found in Excel file');
      }

      const worksheet = workbook.Sheets[sheetName];
      
      // Convert to JSON with header detection
      const jsonData = XLSX.utils.sheet_to_json(worksheet, {
        header: 1, // Use array format first to detect headers
        raw: false, // Convert numbers to strings
        defval: '' // Default value for empty cells
      }) as string[][];

      console.log('Raw Excel data:', jsonData);

      if (jsonData.length === 0) {
        throw new Error('Excel file is empty');
      }

      // Find AUX column
      const headerRow = jsonData[0];
      const auxColumnIndex = this.findAuxColumn(headerRow);
      
      if (auxColumnIndex === -1) {
        throw new Error('AUX column not found. Please ensure your Excel file has a column named "AUX" with employee numbers.');
      }

      console.log(`Found AUX column at index ${auxColumnIndex}`);

      // Extract AUX numbers from data rows
      const auxNumbers: string[] = [];
      const errors: string[] = [];
      let validRows = 0;

      for (let i = 1; i < jsonData.length; i++) { // Skip header row
        const row = jsonData[i];
        const auxValue = row[auxColumnIndex];
        
        if (auxValue && auxValue.toString().trim()) {
          const cleanedValue = this.cleanAuxNumber(auxValue.toString());
          if (cleanedValue) {
            auxNumbers.push(cleanedValue);
            validRows++;
          } else {
            errors.push(`Row ${i + 1}: Invalid AUX format "${auxValue}"`);
          }
        } else {
          // Skip empty rows without error
          continue;
        }
      }

      if (auxNumbers.length === 0) {
        throw new Error('No valid AUX numbers found in the Excel file');
      }

      // Remove duplicates
      const uniqueAuxNumbers = [...new Set(auxNumbers)];
      
      console.log(`Extracted ${uniqueAuxNumbers.length} unique AUX numbers from ${jsonData.length - 1} rows`);

      return {
        success: true,
        auxNumbers: uniqueAuxNumbers,
        errors,
        totalRows: jsonData.length - 1, // Exclude header
        validRows: uniqueAuxNumbers.length
      };

    } catch (error) {
      console.error('Error processing Excel file:', error);
      return {
        success: false,
        auxNumbers: [],
        errors: [error instanceof Error ? error.message : 'Unknown error processing Excel file'],
        totalRows: 0,
        validRows: 0
      };
    }
  }

  /**
   * Find AUX column in header row (case-insensitive)
   */
  private findAuxColumn(headerRow: string[]): number {
    const auxVariations = ['aux', 'AUX', 'Aux', 'aux no', 'aux_no', 'auxno', 'employee_no', 'emp_no', 'pin'];
    
    for (let i = 0; i < headerRow.length; i++) {
      const header = headerRow[i]?.toString().toLowerCase().trim();
      if (auxVariations.some(variation => header === variation.toLowerCase())) {
        return i;
      }
    }
    
    return -1;
  }

  /**
   * Clean and validate AUX number
   */
  private cleanAuxNumber(auxValue: string): string | null {
    if (!auxValue) return null;
    
    // Remove whitespace and common prefixes
    let cleaned = auxValue.toString().trim();
    
    // Remove common prefixes like "EMP", "Employee", etc.
    cleaned = cleaned.replace(/^(emp|employee|staff)\s*/i, '');
    
    // Remove any non-alphanumeric characters except hyphens and underscores
    cleaned = cleaned.replace(/[^\w-]/g, '');
    
    // Validate format - should be numeric or alphanumeric
    if (!/^[a-zA-Z0-9\-_]+$/.test(cleaned)) {
      return null;
    }
    
    // Ensure minimum length
    if (cleaned.length < 1) {
      return null;
    }
    
    return cleaned;
  }

  /**
   * 🆕 ENHANCED: Match AUX numbers with employee database INCLUDING attendance validation
   */
  matchEmployees(auxNumbers: string[], availableEmployees: any[]): EmployeeMatchResult {
    console.log('Matching AUX numbers with employees (with attendance validation):', { 
      auxNumbers, 
      employeeCount: availableEmployees.length 
    });
    
    const matched: any[] = [];
    const failed: string[] = [];
    const matchedIds = new Set<string>();

    // 🆕 NEW: Track attendance status
    const matchedWithAttendance: any[] = [];
    const matchedWithoutAttendance: any[] = [];

    for (const auxNumber of auxNumbers) {
      // Try to find matching employee
      const employee = this.findEmployeeByAux(auxNumber, availableEmployees);
      
      if (employee && !matchedIds.has(employee.id)) {
        matched.push(employee);
        matchedIds.add(employee.id);
        
        // 🆕 NEW: Separate by attendance status
        if (employee.canSelectForOvertime) {
          matchedWithAttendance.push(employee);
        } else {
          matchedWithoutAttendance.push(employee);
        }
        
        console.log(`✓ Matched AUX ${auxNumber} to employee: ${employee.name} (ID: ${employee.id}) - Attendance: ${employee.canSelectForOvertime ? 'Yes' : 'No'}`);
      } else {
        failed.push(auxNumber);
        console.log(`✗ Failed to match AUX ${auxNumber}`);
      }
    }

    console.log(`📊 Excel matching results:`);
    console.log(`   Total matched: ${matched.length}`);
    console.log(`   With attendance: ${matchedWithAttendance.length}`);
    console.log(`   Without attendance: ${matchedWithoutAttendance.length}`);
    console.log(`   Failed: ${failed.length}`);

    return {
      matched,
      failed,
      total: auxNumbers.length,
      success: matched.length > 0,
      // 🆕 NEW: Attendance breakdown
      matchedWithAttendance,
      matchedWithoutAttendance,
      attendanceValidated: true
    };
  }

  /**
   * Find employee by AUX number with multiple matching strategies
   */
  private findEmployeeByAux(auxNumber: string, employees: any[]): any | null {
    const normalizedAux = auxNumber.toLowerCase().trim();
    
    for (const employee of employees) {
      // Strategy 1: Direct PIN match
      if (employee.pin && employee.pin.toString().toLowerCase() === normalizedAux) {
        return employee;
      }
      
      // Strategy 2: Employee number match
      if (employee.employeeNumber && employee.employeeNumber.toString().toLowerCase() === normalizedAux) {
        return employee;
      }
      
      // Strategy 3: Extract number from employee ID (e.g., EMP3576 -> 3576)
      if (employee.id) {
        const idNumber = employee.id.replace(/^emp/i, '').toLowerCase();
        if (idNumber === normalizedAux) {
          return employee;
        }
      }
      
      // Strategy 4: Direct ID match with EMP prefix
      const empId = `emp${normalizedAux}`.toLowerCase();
      if (employee.id && employee.id.toLowerCase() === empId) {
        return employee;
      }
      
      // Strategy 5: Fuzzy matching for common variations
      const employeeVariations = [
        employee.pin?.toString(),
        employee.employeeNumber?.toString(),
        employee.id?.replace(/^emp/i, ''),
        employee.empNo?.toString(),
        employee.staffId?.toString()
      ].filter(Boolean).map(v => v.toLowerCase());
      
      if (employeeVariations.includes(normalizedAux)) {
        return employee;
      }
    }
    
    return null;
  }

  /**
   * Validate Excel file before processing
   */
  validateExcelFile(file: File): { valid: boolean; error?: string } {
    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      return {
        valid: false,
        error: 'File size too large. Please upload a file smaller than 10MB.'
      };
    }

    // Check file type
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel', // .xls
      'application/xlsx',
      'application/xls'
    ];

    if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls)$/i)) {
      return {
        valid: false,
        error: 'Invalid file type. Please upload an Excel file (.xlsx or .xls).'
      };
    }

    return { valid: true };
  }

  /**
   * 🆕 UPDATED: Generate sample Excel template with attendance note
   */
  generateSampleTemplate(): Blob {
    const sampleData = [
      ['AUX', 'Name (Optional)', 'Department (Optional)', 'Notes'],
      ['3576', 'John Doe', 'Engineering', 'Only employees with attendance will be selected'],
      ['1289', 'Jane Smith', 'Operations', 'System validates attendance automatically'],
      ['4521', 'Mike Johnson', 'Maintenance', 'Absent employees will be filtered out'],
      ['7863', 'Sarah Wilson', 'Quality', 'Check attendance status before uploading']
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(sampleData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Employee AUX Numbers');

    // Style the header row
    const headerRange = XLSX.utils.decode_range(worksheet['!ref'] || 'A1:D1');
    for (let col = headerRange.s.c; col <= headerRange.e.c; col++) {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
      if (!worksheet[cellAddress]) continue;
      worksheet[cellAddress].s = {
        font: { bold: true },
        fill: { fgColor: { rgb: "C29E5D" } },
        alignment: { horizontal: "center" }
      };
    }

    // Set column widths
    worksheet['!cols'] = [
      { width: 15 }, // AUX column
      { width: 25 }, // Name column
      { width: 20 }, // Department column
      { width: 40 }  // Notes column
    ];

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    return new Blob([excelBuffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
  }

  /**
   * Download sample template file
   */
  downloadSampleTemplate(): void {
    const blob = this.generateSampleTemplate();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'overtime_employees_template.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * 🆕 ENHANCED: Get detailed statistics including attendance breakdown
   */
  getMatchingStatistics(result: EmployeeMatchResult): {
    successRate: number;
    matchedCount: number;
    failedCount: number;
    totalCount: number;
    suggestions: string[];
    // 🆕 NEW: Attendance statistics
    attendanceStats?: {
      withAttendance: number;
      withoutAttendance: number;
      attendanceRate: number;
    };
  } {
    const successRate = result.total > 0 ? (result.matched.length / result.total) * 100 : 0;
    const suggestions: string[] = [];

    if (result.failed.length > 0) {
      suggestions.push('Check if failed AUX numbers are correct');
      suggestions.push('Verify employees exist in the system');
      suggestions.push('Ensure AUX numbers match the format in MasterSheet');
      
      if (successRate < 50) {
        suggestions.push('Consider downloading and using the sample template');
        suggestions.push('Contact admin to verify employee data format');
      }
    }

    // 🆕 NEW: Add attendance-specific suggestions
    if (result.matchedWithoutAttendance && result.matchedWithoutAttendance.length > 0) {
      suggestions.push(`${result.matchedWithoutAttendance.length} matched employees have no attendance record for the selected date`);
      suggestions.push('Only employees with attendance can be selected for overtime');
      suggestions.push('Consider selecting a different date or verify employee attendance');
    }

    // 🆕 NEW: Calculate attendance statistics
    let attendanceStats;
    if (result.attendanceValidated && result.matchedWithAttendance && result.matchedWithoutAttendance) {
      const withAttendance = result.matchedWithAttendance.length;
      const withoutAttendance = result.matchedWithoutAttendance.length;
      const totalMatched = withAttendance + withoutAttendance;
      
      attendanceStats = {
        withAttendance,
        withoutAttendance,
        attendanceRate: totalMatched > 0 ? Math.round((withAttendance / totalMatched) * 100) : 0
      };
    }

    return {
      successRate: Math.round(successRate * 100) / 100,
      matchedCount: result.matched.length,
      failedCount: result.failed.length,
      totalCount: result.total,
      suggestions,
      attendanceStats
    };
  }

  /**
   * Format AUX numbers for display
   */
  formatAuxForDisplay(auxNumber: string): string {
    // Add leading zeros if numeric and less than 4 digits
    if (/^\d+$/.test(auxNumber) && auxNumber.length < 4) {
      return auxNumber.padStart(4, '0');
    }
    return auxNumber;
  }

  /**
   * 🆕 ENHANCED: Export matching results with attendance status
   */
  exportMatchingResults(result: EmployeeMatchResult, auxNumbers: string[]): Blob {
    const exportData: any[][] = [
      ['AUX Number', 'Status', 'Employee Name', 'Employee ID', 'Department', 'Designation', 'Attendance Status', 'Can Select']
    ];

    // Add matched employees with attendance info
    result.matched.forEach(employee => {
      const auxNumber = auxNumbers.find(aux => 
        this.findEmployeeByAux(aux, [employee]) !== null
      ) || 'Unknown';
      
      // 🆕 NEW: Include attendance information
      const attendanceStatus = employee.attendanceStatus || 'Not Checked';
      const canSelect = employee.canSelectForOvertime ? 'Yes ✓' : 'No ✗';
      
      exportData.push([
        auxNumber,
        'Matched ✓',
        employee.name || 'N/A',
        employee.id || 'N/A',
        employee.department || 'N/A',
        employee.designation || 'N/A',
        attendanceStatus, // 🆕 NEW
        canSelect // 🆕 NEW
      ]);
    });

    // Add failed AUX numbers
    result.failed.forEach(auxNumber => {
      exportData.push([
        auxNumber,
        'Not Found ✗',
        'N/A',
        'N/A',
        'N/A',
        'N/A',
        'N/A', // 🆕 NEW
        'N/A' // 🆕 NEW
      ]);
    });

    const worksheet = XLSX.utils.aoa_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Matching Results');

    // Style the worksheet
    const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1:H1');
    
    // Header styling
    for (let col = range.s.c; col <= range.e.c; col++) {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
      if (!worksheet[cellAddress]) continue;
      worksheet[cellAddress].s = {
        font: { bold: true },
        fill: { fgColor: { rgb: "C29E5D" } },
        alignment: { horizontal: "center" }
      };
    }

    // 🆕 UPDATED: Set column widths for new columns
    worksheet['!cols'] = [
      { width: 15 }, // AUX Number
      { width: 15 }, // Status
      { width: 25 }, // Employee Name
      { width: 15 }, // Employee ID
      { width: 20 }, // Department
      { width: 20 }, // Designation
      { width: 20 }, // Attendance Status - 🆕 NEW
      { width: 15 }  // Can Select - 🆕 NEW
    ];

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    return new Blob([excelBuffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
  }

  /**
   * Download matching results
   */
  downloadMatchingResults(result: EmployeeMatchResult, auxNumbers: string[]): void {
    const blob = this.exportMatchingResults(result, auxNumbers);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `overtime_matching_results_${new Date().toISOString().split('T')[0]}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * 🆕 NEW: Download attendance summary report
   */
  downloadAttendanceSummary(
    result: EmployeeMatchResult, 
    overtimeDate: Date,
    projectName: string
  ): void {
    const exportData: any[][] = [
      [`Overtime Attendance Summary - ${projectName}`],
      [`Date: ${overtimeDate.toLocaleDateString()}`],
      [`Generated: ${new Date().toLocaleString()}`],
      [], // Empty row
      ['Category', 'Count', 'Percentage', 'Employee Names'],
    ];

    const totalMatched = result.matched.length;
    const withAttendance = result.matchedWithAttendance?.length || 0;
    const withoutAttendance = result.matchedWithoutAttendance?.length || 0;

    // Add summary rows
    exportData.push([
      'Total Matched Employees',
      totalMatched,
      '100%',
      result.matched.map(emp => emp.name).join(', ')
    ]);

    exportData.push([
      'With Attendance (Can Select)',
      withAttendance,
      totalMatched > 0 ? `${Math.round((withAttendance / totalMatched) * 100)}%` : '0%',
      result.matchedWithAttendance?.map(emp => emp.name).join(', ') || 'None'
    ]);

    exportData.push([
      'Without Attendance (Cannot Select)',
      withoutAttendance,
      totalMatched > 0 ? `${Math.round((withoutAttendance / totalMatched) * 100)}%` : '0%',
      result.matchedWithoutAttendance?.map(emp => emp.name).join(', ') || 'None'
    ]);

    exportData.push([
      'Failed to Match',
      result.failed.length,
      'N/A',
      result.failed.join(', ')
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Attendance Summary');

    // Set column widths
    worksheet['!cols'] = [
      { width: 30 }, // Category
      { width: 10 }, // Count
      { width: 12 }, // Percentage
      { width: 50 }  // Employee Names
    ];

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `overtime_attendance_summary_${overtimeDate.toISOString().split('T')[0]}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}

export const excelProcessingService = new ExcelProcessingService();